import { lazy, useEffect } from 'react';

import { useFlow } from '@sunflower-games/flow-react';
import { observer } from 'mobx-react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { useBingoWindowWidthRedirect } from '../components/Bingo/hooks/useBingoWindowWidthRedirect';
import { ClaimRewardsAnimation } from '../components/ClaimRewardsAnimation/ClaimRewardsAnimation';
import { MetagamesAssetsPreloader } from '../components/Dialogs/MetagamesDialog/MetagamesAssetsPreloader';
import { FlyingCoinsAnimationComponent } from '../components/FlyingCoins/FlyingCoinsAnimationComponent';
import { Footer } from '../components/Footer/Footer';
import FooterNav from '../components/FooterNav/FooterNav';
import HomeHeader from '../components/headers/HomeHeader';
import HomeHeaderOldSearch from '../components/headers/HomeHeaderOldSearch';
import { Loader } from '../components/Loader/Loader';
import SideMenu from '../components/SideMenu/SideMenu';
import { PRIMARY_HTML_ELEMENT_ID } from '../config/constants';
import { MetaGamesEventType } from '../dto/metagames.types';
import { useAuthFlow } from '../features/auth/useAuthFlow';
import { BingoFTUEView } from '../features/onboarding/bingoFTUE/BingoFTUEView';
import { InteractiveAnimationFTUEView } from '../features/onboarding/interactiveAnimationFTUE/InteractiveAnimationFTUEView';
import { useProcessPaymentRedirect } from '../hooks/useProcessPaymentRedirect';
import { useSlotGameDeepLink } from '../hooks/useSlotGameDeepLink';
import { useSlotGameErrorHandler } from '../hooks/useSlotGameErrorHandler';
import { auth } from '../services/auth';
import { useFlag } from '../services/launchDarkly';
import { lifecycleEventEmitterService } from '../services/lifecycleEventEmitter/LifecycleEventEmitterService';
import { postUiReady } from '../services/nativeBridge/postUiReady';
import { useShellPresence } from '../services/nativeBridge/shellPresence';
import { LandingKey } from '../stores/auth/types';
import { useFeatureManager } from '../stores/feature-manager/useFeatureManager';
import { EnumOpenDialog } from '../stores/ui/types';
import { useStores } from '../stores/useStores';
import { getParam } from '../utils/marketingHelper';

import DisconnectedByMultisession from './DisconnectedByMultisession';
import Home from './Home/Home';
import HomeOld from './HomeOldSearch/Home';
import { toPlatformParam, useNativePlatform } from './NativeLanding/hooks/useNativePlatform';

const Landing = lazy(() => import('./Landing/Landing'));
const NativeLanding = lazy(() => import('./NativeLanding/NativeLanding'));
const Dialogs = lazy(() => import('../components/Dialogs/Dialogs'));
const FullPageDialogs = lazy(() => import('../components/FullPageDialogs/FullPageDialogs'));

const Lobby = observer(() => {
  const { authStore, userStore, bingoStore, uiStore } = useStores();

  const preventMultisession = useFlag('prevent_multisession_enable', false);
  const isOldSearchMode = useFlag('enable_old_search_mode', false);

  const isBingoDialogOpen =
    uiStore.openDialog.dialog === EnumOpenDialog.isOpenMetagamesDialog &&
    uiStore.openDialog.options?.type === MetaGamesEventType.BINGO;

  const { sendEvent } = useFlow();

  useAuthFlow();
  useProcessPaymentRedirect();
  useSlotGameErrorHandler();
  useFeatureManager();
  useBingoWindowWidthRedirect();

  const { isAuthenticationFlowDone, authorized, isDisconnectedByMultisession, isAuthWizardOpen } = authStore;
  const isAuthenticatedAndAllowed = authorized && auth.accessAllowed !== false;

  useSlotGameDeepLink(isAuthenticationFlowDone && isAuthenticatedAndAllowed && !!userStore.profile);

  const searchParams = useSearchParams()[0];
  const landingSearchParam = searchParams.get('landing');
  const nativePlatform = useNativePlatform();
  const shellPresence = useShellPresence();
  const isDirectSignUpLanding = !!landingSearchParam?.startsWith(LandingKey.DIRECT_SU);
  const isDirectEmailLanding = landingSearchParam === LandingKey.DIRECT_EM;
  const isDirectRegLanding = landingSearchParam === LandingKey.DIRECT_REG;
  const isNativeLanding = landingSearchParam === LandingKey.NATIVE;

  useEffect(() => {
    if (userStore.profile) {
      const urlParams = new URLSearchParams(window.location.search);
      sendEvent('app/user.enter_lobby', userStore.profile.firebaseUid, {
        url: window.location.href,
        utm_promo: getParam(urlParams, 'utm_promo') || undefined,
        utm_feature: getParam(urlParams, 'utm_feature') || undefined,
      });
    }
  }, [sendEvent, userStore.profile]);

  useEffect(() => {
    if (isAuthenticationFlowDone && !userStore.loading) {
      lifecycleEventEmitterService.emitMessage('ON_APP_LOBBY_LOADED');
    }
  }, [isAuthenticationFlowDone, userStore.loading]);

  // Not while this renders its own loader for the shell handshake: the shell would drop its branded
  // loader onto this page's generic one — the flash `ui.ready` exists to prevent.
  // Nor while it renders the native lander, which may still be a lazy chunk behind Suspense — NativeLanding
  // posts it once its own content is up.
  const rendersNativeLanding = isNativeLanding && !isAuthenticatedAndAllowed;
  useEffect(() => {
    if (isAuthenticationFlowDone && !userStore.loading && shellPresence !== 'pending' && !rendersNativeLanding) {
      postUiReady();
    }
  }, [isAuthenticationFlowDone, userStore.loading, shellPresence, rendersNativeLanding]);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    const el = document.getElementById(PRIMARY_HTML_ELEMENT_ID);
    if (el) el.scrollTop = 0;

    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Ahead of the isAuthenticationFlowDone loader on purpose: an auth attempt flips that flag back
  // to false, and swapping the native landing for a loader would unmount its flow and lose the
  // current step — the email-verification step after sign-up never got a chance to render.
  // NativeLanding shows its own loader until the flow resolves for the first time.
  // The native lander only opens inside the mobile shell, confirmed by its answer to `app.getInfo`.
  // Anywhere else `landing=native` is dropped so the visitor gets the web landing.
  if (rendersNativeLanding) {
    if (shellPresence === 'pending') {
      return <Loader fullScreen />;
    }
    if (shellPresence === 'absent') {
      const withoutNativeLanding = new URLSearchParams(searchParams);
      withoutNativeLanding.delete('landing');
      return <Navigate replace to={{ pathname: '/', search: withoutNativeLanding.toString() }} />;
    }
    return <NativeLanding />;
  }

  if (!isAuthenticationFlowDone) {
    return <Loader fullScreen />;
  }

  if (preventMultisession && isDisconnectedByMultisession) {
    return <DisconnectedByMultisession />;
  }

  // Held for its wizard — except inside the shell, where the player goes to the native lander below instead.
  if (isDirectRegLanding && !isAuthenticatedAndAllowed && !isAuthWizardOpen && !nativePlatform) {
    return <Loader fullScreen />;
  }

  if (nativePlatform && !isAuthenticatedAndAllowed) {
    // Keeps utm_* and the rest, like the web-landing fallback above.
    const toNativeLanding = new URLSearchParams(searchParams);
    toNativeLanding.set('landing', LandingKey.NATIVE);
    toNativeLanding.set('platform', toPlatformParam(nativePlatform));
    return <Navigate replace to={{ pathname: '/', search: toNativeLanding.toString() }} />;
  }

  if (
    !isDirectEmailLanding &&
    !isDirectSignUpLanding &&
    !isDirectRegLanding &&
    !isNativeLanding &&
    !isAuthenticatedAndAllowed
  ) {
    return <Landing />;
  }

  if (userStore.loading) {
    return <Loader fullScreen />;
  }

  return (
    <>
      {isAuthenticatedAndAllowed && !isBingoDialogOpen && (
        <>
          {bingoStore.isInsideBingoArea ? null : isOldSearchMode ? <HomeHeaderOldSearch /> : <HomeHeader />}
          <SideMenu />
          {isOldSearchMode ? <HomeOld /> : <Home />}
          <MetagamesAssetsPreloader />
          <FlyingCoinsAnimationComponent />
          <ClaimRewardsAnimation />
          {isOldSearchMode && <Footer />}
          <InteractiveAnimationFTUEView />
          <BingoFTUEView entryPoint={bingoStore.bingoFtueEntryPoint} />
        </>
      )}
      <Dialogs />
      <FullPageDialogs />
      {isAuthenticatedAndAllowed && <FooterNav />}
    </>
  );
});

export default Lobby;
